/**
 * Scroll reveals, split-text headline reveals, magnetic buttons, testimonials
 * cycler, count-up stats, rack filter/sort, and light GSAP/Lenis progressive
 * enhancement. Every effect here has a static, correct fallback with JS/GSAP
 * absent or prefers-reduced-motion set.
 */
(function () {
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* ---- scroll reveal ------------------------------------------------- */
  function initReveal() {
    var items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
        el.style.transitionDelay = (delay * 90) + 'ms';
        el.classList.add('is-revealed');
        io.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---- split-text headline reveal ------------------------------------ */
  function initSplitReveal() {
    var items = document.querySelectorAll('[data-split-reveal]');
    if (!items.length || !window.ShuttleYard || !window.ShuttleYard.splitText) {
      items.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    items.forEach(function (el) { window.ShuttleYard.splitText(el, { by: 'word' }); });
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---- magnetic buttons ------------------------------------------------ */
  function initMagnetic() {
    if (!fine || reduced) return;
    var buttons = document.querySelectorAll('[data-magnetic]');
    buttons.forEach(function (btn) {
      var raf = null;
      btn.addEventListener('mousemove', function (e) {
        var rect = btn.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var threshold = Math.max(rect.width, rect.height) / 2 + 40;
        var dist = Math.hypot(dx, dy);
        if (dist > threshold) return;
        var pull = 0.35;
        var x = Math.max(-8, Math.min(8, dx * pull));
        var y = Math.max(-8, Math.min(8, dy * pull));
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          btn.style.transform = 'translate(' + x + 'px,' + y + 'px)';
        });
      });
      btn.addEventListener('mouseleave', function () {
        if (raf) cancelAnimationFrame(raf);
        btn.style.transform = '';
      });
    });
  }

  /* ---- testimonials cycler -------------------------------------------- */
  function initTestimonials() {
    var track = document.querySelector('[data-testimonials]');
    if (!track) return;
    var quotes = track.querySelectorAll('[data-testimonial]');
    var dots = document.querySelectorAll('[data-testimonial-dot]');
    if (quotes.length < 2) return;
    var index = 0;
    var timer = null;

    function show(i) {
      quotes.forEach(function (q, qi) { q.classList.toggle('is-active', qi === i); });
      dots.forEach(function (d, di) { d.classList.toggle('is-active', di === i); });
      index = i;
    }
    function next() { show((index + 1) % quotes.length); }
    function start() {
      if (reduced) return;
      stop();
      timer = setInterval(next, 5000);
    }
    function stop() { if (timer) clearInterval(timer); }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { show(i); start(); });
    });
    start();
  }

  /* ---- count-up ---------------------------------------------------------- */
  function initCountUp() {
    var items = document.querySelectorAll('[data-count-up]');
    if (!items.length || reduced || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseFloat(el.getAttribute('data-count-to'));
        if (isNaN(target)) return;
        var numEl = el.querySelector('.stat-num') || el;
        var start = performance.now();
        var duration = 1200;
        function tick(now) {
          var p = Math.min(1, (now - start) / duration);
          var eased = 1 - Math.pow(1 - p, 3);
          numEl.textContent = Math.round(target * eased).toLocaleString('en-US');
          if (p < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---- rack filter / sort ------------------------------------------------ */
  function initRackFilters() {
    var grid = document.querySelector('[data-rack-grid]');
    var filterGroups = document.querySelectorAll('[data-filter-group]');
    var sortSelect = document.querySelector('[data-rack-sort]');
    if (!grid) return;
    var items = Array.prototype.slice.call(grid.querySelectorAll('[data-rack-item]'));
    var state = { vendor: 'all', style: 'all' };

    var params = new URLSearchParams(window.location.search);
    if (params.get('style')) state.style = params.get('style');

    function applyFilters() {
      items.forEach(function (item) {
        var matchVendor = state.vendor === 'all' || item.getAttribute('data-vendor') === state.vendor;
        var matchStyle = state.style === 'all' || item.getAttribute('data-style') === state.style;
        item.classList.toggle('is-filtered-out', !(matchVendor && matchStyle));
      });
    }

    function applySort(value) {
      var sorted = items.slice();
      if (value === 'price-asc') sorted.sort(function (a, b) { return a.getAttribute('data-price') - b.getAttribute('data-price'); });
      else if (value === 'price-desc') sorted.sort(function (a, b) { return b.getAttribute('data-price') - a.getAttribute('data-price'); });
      sorted.forEach(function (item) { grid.appendChild(item); });
    }

    filterGroups.forEach(function (group) {
      var key = group.getAttribute('data-filter-group');
      group.querySelectorAll('[data-filter-option]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          group.querySelectorAll('[data-filter-option]').forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          state[key] = btn.getAttribute('data-value');
          applyFilters();
        });
      });
      if (key === 'style' && state.style !== 'all') {
        group.querySelectorAll('[data-filter-option]').forEach(function (b) {
          b.classList.toggle('is-selected', b.getAttribute('data-value') === state.style);
        });
      }
    });

    if (sortSelect) {
      sortSelect.addEventListener('change', function () { applySort(sortSelect.value); });
    }

    applyFilters();
  }

  /* ---- Lenis smooth scroll + GSAP progressive enhancement ---------------- */
  function initScrollFx() {
    if (reduced) return;
    var hasGsap = !!window.gsap;
    var hasScrollTrigger = !!window.ScrollTrigger;
    var hasLenis = !!window.Lenis;

    if (hasGsap && hasScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);

    if (hasLenis) {
      var lenis = new window.Lenis({ lerp: 0.09, wheelMultiplier: 1 });
      function raf(time) {
        lenis.raf(time);
        if (hasScrollTrigger) window.ScrollTrigger.update();
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
      if (hasGsap) {
        window.gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
        window.gsap.ticker.lagSmoothing(0);
      }
    }

    if (!hasGsap || !hasScrollTrigger || !fine) return;

    /* hero: subtle parallax on the bg media while pinned content is visible */
    var heroMedia = document.querySelector('[data-hero-bg]');
    if (heroMedia) {
      window.gsap.to(heroMedia, {
        yPercent: 15,
        ease: 'none',
        scrollTrigger: { trigger: heroMedia.closest('section'), start: 'top top', end: 'bottom top', scrub: true },
      });
    }

    /* craft banner: stagger the three stat blocks in as the section pins briefly */
    var craftStats = document.querySelectorAll('[data-craft-fill-1], [data-craft-fill-2], [data-craft-fill-3]');
    if (craftStats.length) {
      window.gsap.from(craftStats, {
        opacity: 0,
        y: 32,
        stagger: 0.15,
        duration: 0.8,
        ease: 'expo.out',
        scrollTrigger: { trigger: craftStats[0].closest('section'), start: 'top 70%' },
      });
    }
  }

  ready(function () {
    initReveal();
    initSplitReveal();
    initMagnetic();
    initTestimonials();
    initCountUp();
    initRackFilters();
    initScrollFx();
  });
})();
